import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
  LOCALE_FLAG,
  LOCALE_NATIVE_NAME,
  SUPPORTED_LOCALES,
  setLocale,
  type SupportedLocale,
} from '@/i18n';
import { isMuted, toggleMuted, unlock } from '@/lib/sound';
import { useCafe3dStore } from '@/stores/cafe3dStore';
import { BeanCounter } from './BeanCounter';

export function HudBar() {
  const { t } = useTranslation();
  const beanCoin = useCafe3dStore((s) => s.beanCoin);
  const reputation = useCafe3dStore((s) => s.reputation);
  const energy = useCafe3dStore((s) => s.energy);
  const energyMax = useCafe3dStore((s) => s.energyMax);
  const streak = useCafe3dStore((s) => s.currentStreak);

  return (
    <div className="h-[60px] px-3 flex items-center justify-between bg-cream-100/95 backdrop-blur-sm border-b border-coffee-700/10 game-ui">
      <div className="flex items-center gap-3">
        <HudSlot icon="☕" value={<BeanCounter value={beanCoin} className="font-baloo font-semibold text-coffee-900 text-lg" />} label={t('hud.bean')} />
        <div className="w-px h-7 bg-coffee-700/15" />
        <HudSlot icon="⭐" value={<BeanCounter value={reputation} className="font-baloo font-semibold text-coffee-900 text-lg" />} label={t('hud.reputation')} />
        <div className="w-px h-7 bg-coffee-700/15" />
        <EnergySlot energy={energy} max={energyMax} />
      </div>
      <div className="flex items-center gap-2">
        {streak > 0 && <StreakBadge streak={streak} label={t('hud.streak')} />}
        <LanguageToggle />
        <MuteToggle />
      </div>
    </div>
  );
}

function StreakBadge({ streak, label }: { streak: number; label: string }) {
  return (
    <div
      className="flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-light/30 border border-yellow-light/60"
      title={label}
    >
      <span className="text-base">🔥</span>
      <span className="text-sm font-baloo font-bold text-coffee-900 tabular-nums">
        {streak}
      </span>
    </div>
  );
}

function LanguageToggle() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = i18n.language as SupportedLocale;

  // Render dropdown qua React Portal → thoát khỏi:
  //   1. `overflow-hidden` của parent MainCafeScene (clip menu)
  //   2. Stacking context của Three.js Canvas (chặn pointer events)
  // Anchor `position: fixed` top-right viewport — HudBar luôn fixed top.
  const dropdown =
    open &&
    createPortal(
      <>
        <div
          aria-hidden="true"
          className="fixed inset-0 z-[9998]"
          onClick={() => setOpen(false)}
        />
        <div
          className="fixed top-[56px] right-3 z-[9999] min-w-[160px] bg-white rounded-xl shadow-soft-amber border border-coffee-700/10 overflow-hidden"
        >
          {SUPPORTED_LOCALES.map((locale) => {
            const active = locale === current;
            return (
              <button
                key={locale}
                onClick={() => {
                  setLocale(locale);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-cream transition ${
                  active ? 'bg-cream-100 font-medium text-coffee-900' : 'text-coffee-700'
                }`}
              >
                <span className="text-base">{LOCALE_FLAG[locale]}</span>
                <span>{LOCALE_NATIVE_NAME[locale]}</span>
                {active && <span className="ml-auto text-coffee">✓</span>}
              </button>
            );
          })}
        </div>
      </>,
      document.body,
    );

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 flex items-center justify-center rounded-full bg-coffee-900/5 hover:bg-coffee-900/10 active:scale-95 transition"
        aria-label={t('language.select_title')}
        aria-expanded={open}
      >
        <span className="text-base">{LOCALE_FLAG[current] ?? '🌐'}</span>
      </button>
      {dropdown}
    </>
  );
}

function MuteToggle() {
  const { t } = useTranslation();
  const [muted, setMuted] = useState(() => isMuted());
  return (
    <button
      onClick={() => {
        unlock();
        setMuted(toggleMuted());
      }}
      className="w-9 h-9 flex items-center justify-center rounded-full bg-coffee-900/5 hover:bg-coffee-900/10 active:scale-95 transition"
      aria-label={muted ? t('hud.mute_on') : t('hud.mute_off')}
      aria-pressed={muted}
    >
      <span className="text-base">{muted ? '🔇' : '🔊'}</span>
    </button>
  );
}

function HudSlot({ icon, value, label }: { icon: string; value: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-base">{icon}</span>
      <div className="flex flex-col leading-tight">
        <div className="-mb-0.5">{value}</div>
        <span className="text-[9px] text-coffee-700/60 uppercase tracking-wide">{label}</span>
      </div>
    </div>
  );
}

function EnergySlot({ energy, max }: { energy: number; max: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-base">⚡</span>
      <div className="flex flex-col leading-tight">
        <span
          className={`font-baloo font-semibold text-lg tabular-nums ${
            energy === 0 ? 'text-red-stool' : 'text-coffee-900'
          }`}
        >
          {energy}/{max}
        </span>
        <div className="flex gap-0.5 mt-0.5">
          {Array.from({ length: max }).map((_, i) => (
            <div
              key={i}
              className={`w-1.5 h-1 rounded-full ${
                i < energy ? 'bg-yellow-light' : 'bg-coffee-700/15'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
